import { computed, ref } from 'vue'
import { isArray, isFunction } from '@/global/utils/utils'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { useTableService } from '@/global/composables/table/useTableService'
import { showDetailsAction } from '@/global/components/table/helpers/tableActions'
import { FORMATS } from '@/global/services/date'
import IrregularitiesMapPanel from '@/modules/road-maintenance-module/winter-service-v2/irregularities/components/IrregularitiesMapPanel.vue'
import { toReportedAtLabel } from '@/modules/road-maintenance-module/winter-service-v2/irregularities/composables/irregularityData'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  IRREGULARITIES_ENDPOINT,
  IRREGULARITIES_TABLE_KEY,
  IRREGULARITIES_TRANSLATION_NAMESPACE as NS,
  IRREGULARITIES_UI_VIEW,
  IRREGULARITY_DETAILS_ROUTE,
  IRREGULARITY_TYPES_AUTOCOMPLETE_ENDPOINT
} from '@/modules/road-maintenance-module/winter-service-v2/irregularities/config/constants'

const { buildConfig } = useTableService()

const API_CONFIG = {
  get: {
    module: WINTER_SERVICE_MODULE,
    route: IRREGULARITIES_ENDPOINT
  }
}

export function useTable (options) {
  const { trans, router } = options || {}

  // The rows the user marked. They are the whole of what the map draws - both the
  // pins and the routes - so the panel needs nothing else and no request of its own.
  const markedIrregularities = ref([])

  const fetchTableData = () => {
    const { fetchData } = registeredTableHandlers?.[IRREGULARITIES_TABLE_KEY] || {}
    if (!isFunction(fetchData)) {
      return
    }
    fetchData()
  }

  const onShowIrregularityDetails = (event) => {
    const { id } = event?.data || {}
    if (!id) {
      return
    }

    router.push({
      name: IRREGULARITY_DETAILS_ROUTE,
      params: {
        id: String(id)
      }
    })
  }

  // The listing reports an irregularity nested - the type, the vehicle and the driver
  // each as their own object - so the columns read their value off those.
  const cellsConfig = computed(() => ({
    vehicle: {
      custom_display_value: (data) => data?.vehicle?.registration || ''
    },
    driver: {
      custom_display_value: (data) => data?.driver?.full_name || ''
    },
    irregularity_type: {
      custom_display_value: (data) => data?.type?.description || ''
    },
    reported_at: {
      custom_display_value: (data) => toReportedAtLabel(data) || '-'
    }
  }))

  const headers = computed(() => ([
    {
      label: trans(`${NS}.id`),
      key: 'id',
      always_visible: true
    },
    {
      label: trans(`${NS}.vehicle`),
      key: 'vehicle',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.driver`),
      key: 'driver',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.reported_at`),
      key: 'reported_at',
      always_visible: true
    },
    {
      label: trans(`${NS}.irregularity_type`),
      key: 'irregularity_type',
      always_visible: true
    }
  ]))

  // Only what the listing endpoint understands. It matches on ids rather than text,
  // so each of these is a picker or a date.
  const filtersConfig = computed(() => ({
    fields: [
      {
        key: 'type_id',
        label: trans(`${NS}.irregularity_type`),
        type: 'autocomplete',
        autocomplete_options: {
          module: WINTER_SERVICE_MODULE,
          route: IRREGULARITY_TYPES_AUTOCOMPLETE_ENDPOINT
        }
      },
      {
        key: 'vehicle_id',
        label: trans(`${NS}.vehicle`),
        type: 'autocomplete',
        autocomplete_options: {
          module: 'fleet',
          route: 'vehicles/autocomplete'
        }
      },
      {
        key: 'driver_id',
        label: trans(`${NS}.driver`),
        type: 'autocomplete',
        autocomplete_options: {
          module: 'people',
          route: 'people/autocomplete'
        }
      },
      {
        // The endpoint reads these as plain dates and widens them to whole days
        // itself, so no time part is sent.
        key: 'date_from',
        label: trans(`${NS}.date_from`),
        type: 'datetime-picker',
        subType: 'date',
        appendIcon: 'mdi-calendar-month',
        placeholder: FORMATS.date
      },
      {
        key: 'date_to',
        label: trans(`${NS}.date_to`),
        type: 'datetime-picker',
        subType: 'date',
        appendIcon: 'mdi-calendar-month',
        placeholder: FORMATS.date
      }
    ]
  }))

  // Reports arrive from the drivers' devices, so there is nothing to create, edit or
  // delete here - only inspect. A row click marks the report on the map instead, so
  // this action is the way into its details.
  const actionsConfig = computed(() => ([
    showDetailsAction(trans, onShowIrregularityDetails)
  ]))

  const breadcrumbs = computed(() => ({
    items: [
      {
        text: trans('road-maintenance/winter_service2.irregularities')
      },
      {
        text: trans(`${NS}.inspection`),
        bolded: true
      }
    ]
  }))

  const config = computed(() => ({
    tableConfig: buildConfig({
      key: IRREGULARITIES_TABLE_KEY,
      uiView: IRREGULARITIES_UI_VIEW,
      apiConfig: API_CONFIG,
      // Clicking a row is what marks it - it is how the map is told which reports to
      // draw. Opening a report is the double click, the same as on the work orders
      // listing, and the row's action menu keeps it reachable either way.
      rowDblClick: onShowIrregularityDetails,
      // A click on a row shows it on the map beside the table
      rowClickIndicator: 'map',
      search_and_buttons_config: {
        // Free text over the columns the table shows - the vehicle, the driver, the
        // type and the task it was reported against. The filters dialog stays for the
        // narrower, per-field lookups.
        search_field_config: {
          placeholder: trans('base.tracker_search'),
          prependInnerIcon: 'mdi-magnify'
        },
        showNewBtn: false
      },
      rowsSelection: {
        // Picking rows is what this table's row click is for - it is how the map is
        // told which reports to draw - so the checkbox column is not needed for it.
        showCheckboxes: false,
        // Rows are keyed by the report's id rather than by where they sit, so the map
        // and the table cannot disagree about which report is picked.
        rememberSelectionByDataProperty: 'id',
        selectionsChange: ({ data }) => {
          markedIrregularities.value = isArray(data) ? data : []
        }
      },
      sideTableComponent: {
        component: IrregularitiesMapPanel,
        visible: true,
        irregularities: markedIrregularities.value
      },
      globalStyle: {
        sideTableContentColClasses: 'irregularities-map-col'
      },
      tableConfig: {
        cellsConfig: cellsConfig.value,
        headers: headers.value,
        actionsConfig: actionsConfig.value,
        filtersConfig: filtersConfig.value
      }
    }),
    breadcrumbs: breadcrumbs.value
  }))

  return {
    config,
    fetchTableData
  }
}
